/* eslint-disable import/no-duplicates */
import FullCalendar from "@fullcalendar/react";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { addLocale } from "primereact/api";
import {
    createRef,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useState,
} from "react";

import Context from "../context/Context";
import { ptBrDatepickerTranslations } from "../i18n/locales/pt-br";
import {
    StyledLabelStrong,
    StyledCalendar,
    LegendItem,
    StyledLegendDiv,
    StyledLegendChildren,
    StyledColorLegend,
} from "../styles/components/FullCalendarStyles";
import StringUtils from "../utils/types/StringUtils";
import { StyleWrapper } from "../styles/pages/Schedule/ScheduleClassesStyle";
import { StyledFlexDiv } from "../styles/MainStyles";

addLocale("pt-BR", ptBrDatepickerTranslations);

interface CalendarProps {
    data: any;
    setInitialDate: Dispatch<SetStateAction<null>>;
    setFinalDate: Dispatch<SetStateAction<null>>;
    addHoliday: (dateString: string) => void;
    editHoliday: (holydayId: string, obj: any) => void;
}

const wekeendNames = ["SEG", "TER", "QUA", "QUI", "SEX", "SAB", "DOM"];

function FullCalendarApp({
    data = [],
    setInitialDate,
    setFinalDate,
    addHoliday,
    editHoliday,
}: CalendarProps) {
    const [period, setPeriod] = useState<any>(new Date());
    const [atualizandoPeriodo, setAtualizandoPeriodo] = useState(false);
    const [atualDate] = useState<any>(new Date());
    const [calendarComponentRef] = useState<any>(createRef());
    const [dateFormatValue, setDateFormatValue] = useState("");
    const [isVisible, setIsVisible] = useState(false);

    const { openMenu } = useContext(Context);

    const configurarDate = (periodRef: any) => {
        if (!atualizandoPeriodo) {
            setAtualizandoPeriodo(true);
            setTimeout(() => {
                // TODO: Verificar se o código abaixo resolveu o erro Cannot read properties of null (reading 'getApi')
                try {
                    const calendarApi = calendarComponentRef.current?.getApi();
                    if (calendarApi) {
                        calendarApi.gotoDate(periodRef);
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error(error);
                } finally {
                    setTimeout(() => {
                        setAtualizandoPeriodo(false);
                    }, 100);
                }
            }, 500);
        }
    };

    useEffect(() => {
        configurarDate(period);
    }, [period]);

    const handleDataSet = (event: any) => {
        const initialDateSubstring = event.startStr.substring(0, 10);
        const finalDateSubstring = event.endStr.substring(0, 10);

        setInitialDate(initialDateSubstring);
        setFinalDate(finalDateSubstring);

        let month = new Date(event.endStr).getMonth();
        let year = new Date(event.endStr).getFullYear();
        year = month === 0 ? year - 1 : year;
        month = month === 0 ? 12 : month;

        const newDate = new Date(`${year}-${month}-15`);
        const date = format(newDate, "MMMM/yyyy", { locale: ptBR });
        setDateFormatValue(date);
        setPeriod(newDate);
    };

    const handleChangeDate = (event: any) => {
        const date = format(event.value, "MMMM/yyyy", { locale: ptBR });
        setDateFormatValue(date);
        setPeriod(new Date(event.value));
    };

    const handleVisibleChange = () => {
        setIsVisible(!isVisible);
    };

    const handleVisibility = () => {
        setIsVisible(!isVisible);
    };

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    position: "absolute",
                    left: openMenu ? "calc(300px + 5%)" : "calc(84px + 5%)",
                    paddingTop: 18,
                    visibility: "hidden",
                }}
            >
                <StyledCalendar
                    value={period}
                    onChange={handleChangeDate}
                    view="month"
                    locale="pt-BR"
                    dateFormat="MM/yy"
                    yearNavigator
                    yearRange="2010:2030"
                    style={{ border: "transparent" }}
                    showOnFocus={false}
                    visible={isVisible}
                    onVisibleChange={handleVisibleChange}
                />
            </div>
            <StyledLegendDiv>
                <StyledLegendChildren>
                    <StyledFlexDiv>
                        <StyledLabelStrong>Legenda: </StyledLabelStrong>
                    </StyledFlexDiv>
                    <StyledColorLegend>
                        <LegendItem color="#0B8043" />
                        <StyledLabelStrong>Feriado Nacional</StyledLabelStrong>
                    </StyledColorLegend>
                    <StyledColorLegend>
                        <LegendItem color="#E25E2F" />
                        <StyledLabelStrong>Feriado Municipal</StyledLabelStrong>
                    </StyledColorLegend>
                </StyledLegendChildren>
                <StyledLegendChildren>
                    <StyledColorLegend>
                        <LegendItem color="#449ADF" />
                        <StyledLabelStrong>Recesso</StyledLabelStrong>
                    </StyledColorLegend>
                    <StyledColorLegend>
                        <LegendItem color="#2F6A99" />
                        <StyledLabelStrong>
                            Abono pela administração
                        </StyledLabelStrong>
                    </StyledColorLegend>
                    <StyledColorLegend>
                        <LegendItem color="#A16000" />
                        <StyledLabelStrong>
                            Gestão de Atividades
                        </StyledLabelStrong>
                    </StyledColorLegend>
                </StyledLegendChildren>
            </StyledLegendDiv>
            <StyleWrapper>
                <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    locale={ptBrLocale}
                    initialView="dayGridMonth"
                    dayHeaderFormat={(param) => {
                        return wekeendNames[param.date.marker.getDay()];
                    }}
                    headerToolbar={{
                        start: "prev,next pickCalendar,icon",
                        center: "",
                        end: "",
                    }}
                    customButtons={{
                        pickCalendar: {
                            text: StringUtils.capitalizeFirstLetter(
                                dateFormatValue
                            ),
                            click() {
                                handleVisibility();
                            },
                        },
                        icon: {
                            text: "",
                            click() {
                                handleVisibility();
                            },
                            icon: !isVisible
                                ? "pi pi pi-angle-down"
                                : "pi pi pi-angle-up",
                        },
                    }}
                    initialDate={atualDate}
                    ref={calendarComponentRef}
                    events={data}
                    firstDay={1}
                    nowIndicator
                    datesSet={handleDataSet}
                    dateClick={(e: any) => addHoliday(e.dateStr)}
                    eventClick={(e: any) => editHoliday(e.event.id, e.event)}
                    height="650px"
                    showNonCurrentDates={false}
                />
            </StyleWrapper>
        </div>
    );
}

export default FullCalendarApp;
